import { createPortal } from "react-dom";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";

import LevelDeletionMessage from "./level-delete-message";

// Deleting a level from a page of competencies: clear it from the ones on the
// page and leave everything else where it is, or take the level out of the
// whole ladder. Each choice sits with what it does, so the button pressed is
// the one whose consequences were just read.
const LevelDeleteChoiceDialog = ({
  level,
  pageTitle,
  toClear,
  deleted,
  onClear,
  onDeleteEverywhere,
  onCancel,
}: {
  level: number;
  // Where clearing reaches, e.g. "Company-Wide Competencies".
  pageTitle: string;
  // The competencies on the page with criteria on this level.
  toClear: ReadonlyArray<{ id: number; title: string }>;
  // What deleting everywhere takes, from getLevelDeletionSummary.
  deleted: ReadonlyArray<string>;
  onClear: () => void;
  onDeleteEverywhere: () => void;
  onCancel: () => void;
}) => {
  const label = useLabel();
  return createPortal(
    <Modal open onClose={onCancel} alignedTop aria-label="Delete level dialog">
      <div className="p-6 flex flex-col gap-4 text-sm">
        <ModalTitle onClose={onCancel}>Delete level {level}?</ModalTitle>

        <div className="rounded-md border p-4 flex flex-col gap-3">
          <div className="font-semibold text-gray-900">
            Clear level {level} from {pageTitle}
          </div>
          {toClear.length > 0 ? (
            <div className="flex flex-col gap-2">
              <p>
                This deletes the level {level} criteria from{" "}
                {toClear.length === 1
                  ? `this ${label("competency")}`
                  : `these ${label("competency", { pluralize: true })}`}
                , and the {label("review")} questions and answers about them:
              </p>
              <ul className="list-disc pl-5 flex flex-col gap-1">
                {toClear.map(({ id, title }) => (
                  <li key={id}>{title}</li>
                ))}
              </ul>
              <p>
                A {label("competency")} is the same wherever it&apos;s used, so
                it loses them there too. Other{" "}
                {label("competency", { pluralize: true })}, roles and levels
                stay where they are.
              </p>
            </div>
          ) : (
            <p className="text-gray-500">
              Nothing in {pageTitle} has level {level} criteria.
            </p>
          )}
          <div className="flex justify-end">
            <Button
              theme={buttonTheme.primary}
              disabled={toClear.length === 0}
              text={`Clear level ${level}`}
              aria-label="Delete level dialog: Clear here"
              onClick={onClear}
            />
          </div>
        </div>

        <div className="rounded-md border p-4 flex flex-col gap-3">
          <div className="font-semibold text-gray-900">
            Delete level {level} everywhere
          </div>
          <LevelDeletionMessage level={level} deleted={deleted} />
          <div className="flex justify-end">
            <Button
              theme={buttonTheme.redDanger}
              text={`Delete level ${level} everywhere`}
              aria-label="Delete level dialog: Delete everywhere"
              onClick={onDeleteEverywhere}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button theme={buttonTheme.text} text="Cancel" onClick={onCancel} />
        </div>
      </div>
    </Modal>,
    document.body,
  );
};

export default LevelDeleteChoiceDialog;
